import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Search, Trash2, Upload, Download, Loader2, Tag as TagIcon, X, ChevronLeft, ChevronRight,
} from "lucide-react";
import { LEAD_STATUSES, statusByValue } from "@/lib/constants";
import { groupTagsByCategory } from "@/lib/tagUtils";
import { useConfirm } from "@/hooks/useConfirm";

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [tags, setTags] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const confirm = useConfirm();

  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", country: "", lead_source: "", status: "new_lead" });

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (search) params.search = search;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (tagFilter && tagFilter !== "all") params.tag_id = tagFilter;
      const { data } = await api.get("/contacts", { params });
      setContacts(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, statusFilter, tagFilter, offset]);
  useEffect(() => { api.get("/tags").then(({ data }) => setTags(data)); }, []);

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === contacts.length) setSelected(new Set());
    else setSelected(new Set(contacts.map((c) => c.id)));
  };

  const handleCreate = async () => {
    if (!form.name || !form.phone) return toast.error("Name and phone required");
    try {
      await api.post("/contacts", { ...form, tag_ids: [], custom_fields: {} });
      toast.success("Contact created");
      setOpenCreate(false);
      setForm({ name: "", phone: "", email: "", city: "", country: "", lead_source: "", status: "new_lead" });
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    const ok = await confirm({
      title: `Delete ${selected.size} contact${selected.size > 1 ? "s" : ""}?`,
      description: "Selected contacts will be permanently removed.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.post("/contacts/bulk-delete", Array.from(selected));
    toast.success("Deleted");
    setSelected(new Set());
    load();
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/contacts/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.created} (${data.skipped} skipped)`);
      load();
    } catch {
      toast.error("Import failed");
    }
    e.target.value = "";
  };

  const handleExport = async () => {
    const token = localStorage.getItem("flowsend_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts/export/csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contacts.csv";
    a.click();
  };

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="contacts-page">
      <PageHeader
        title="Contacts"
        description={`${total} total contacts in your workspace`}
        actions={
          <>
            <label className="cursor-pointer">
              <input type="file" accept=".csv" className="hidden" onChange={handleImport} data-testid="contacts-import-input" />
              <Button variant="outline" asChild data-testid="contacts-import-button">
                <span><Upload className="w-4 h-4 mr-1.5" />Import</span>
              </Button>
            </label>
            <Button variant="outline" onClick={handleExport} data-testid="contacts-export-button">
              <Download className="w-4 h-4 mr-1.5" />Export
            </Button>
            <Button
              onClick={() => setOpenCreate(true)}
              data-testid="contacts-new-button"
              className="bg-brand hover:bg-brand-dark"
            >
              <Plus className="w-4 h-4 mr-1.5" />New contact
            </Button>
          </>
        }
      />

      <Card className="border-slate-200 shadow-none">
        <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name, phone, email…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
              className="pl-9"
              data-testid="contacts-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setOffset(0); }}>
            <SelectTrigger className="w-44" data-testid="contacts-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {LEAD_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tagFilter} onValueChange={(v) => { setTagFilter(v); setOffset(0); }}>
            <SelectTrigger className="w-44" data-testid="contacts-tag-filter">
              <SelectValue placeholder="Tag" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tags</SelectItem>
              {groupTagsByCategory(tags, { excludeInactive: true }).map(({ category, tags: catTags }) => (
                <div key={category}>
                  <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{category}</div>
                  {catTags.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </div>
              ))}
            </SelectContent>
          </Select>
          {selected.size > 0 && (
            <Button variant="outline" onClick={handleBulkDelete} className="text-red-600 ml-auto" data-testid="contacts-bulk-delete">
              <Trash2 className="w-4 h-4 mr-1.5" />Delete ({selected.size})
            </Button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200">
                <th className="p-3 w-10">
                  <Checkbox
                    checked={selected.size === contacts.length && contacts.length > 0}
                    onCheckedChange={toggleAll}
                    data-testid="contacts-select-all"
                  />
                </th>
                <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Name</th>
                <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Phone</th>
                <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Email</th>
                <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">City</th>
                <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Source</th>
                <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Status</th>
                <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></td></tr>
              ) : contacts.length === 0 ? (
                <tr><td colSpan={8} className="p-12 text-center text-slate-400 text-sm">No contacts yet. Add one or import from CSV.</td></tr>
              ) : contacts.map((c) => {
                const status = statusByValue(c.status);
                return (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`contact-row-${c.id}`}>
                    <td className="p-3">
                      <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleSelect(c.id)} />
                    </td>
                    <td className="p-3">
                      <Link to={`/contacts/${c.id}`} className="font-medium text-slate-900 hover:text-brand-dark" data-testid={`contact-name-${c.id}`}>
                        {c.name}
                      </Link>
                    </td>
                    <td className="p-3 font-mono text-xs text-slate-600">{c.phone}</td>
                    <td className="p-3 text-slate-600">{c.email || "—"}</td>
                    <td className="p-3 text-slate-600">{c.city || "—"}</td>
                    <td className="p-3 text-slate-600">{c.lead_source || "—"}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="p-3 border-t border-slate-200 flex items-center justify-between text-sm">
          <div className="text-xs text-slate-500">
            Showing {offset + 1}–{Math.min(offset + limit, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} data-testid="contacts-prev-page">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} data-testid="contacts-next-page">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {[
              { key: "name", label: "Name *" },
              { key: "phone", label: "Phone *" },
              { key: "email", label: "Email" },
              { key: "city", label: "City" },
              { key: "country", label: "Country" },
              { key: "lead_source", label: "Lead source" },
            ].map((f) => (
              <div key={f.key}>
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">{f.label}</Label>
                <Input
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1.5"
                  data-testid={`new-contact-${f.key}`}
                />
              </div>
            ))}
            <div>
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="new-contact-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-brand hover:bg-brand-dark" data-testid="new-contact-save">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
